import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';

const statusColor = { ACTIVE: 'badge-green', CANCELLED: 'badge-red', REFUNDED: 'badge-yellow', VOID: 'badge-gray' };
const PAGE_SIZE = 20;

export default function TicketList() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);

    // If searching, check if query matches any client names first
    let clientIds = [];
    if (q) {
      const { data: matchedClients } = await supabase
        .from('clients').select('id').ilike('full_name', `%${q}%`).limit(50);
      clientIds = (matchedClients || []).map(c => c.id);
    }

    let query = supabase.from('tickets')
      .select('*, clients(full_name), cost_currency:cost_currency_id(code), sell_currency:sell_currency_id(code), invoice:invoice_id(id, invoice_number)', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

    if (q) {
      const orParts = `ticket_ref.ilike.%${q}%,pnr.ilike.%${q}%,ticket_number.ilike.%${q}%,airline.ilike.%${q}%,passenger_name.ilike.%${q}%`;
      if (clientIds.length > 0) {
        query = query.or(`${orParts},client_id.in.(${clientIds.join(',')})`);
      } else {
        query = query.or(orParts);
      }
    }

    const { data, count } = await query;
    setTickets(data || []);
    setTotal(count || 0);
    setLoading(false);
  }, [q, page]);

  useEffect(() => { setPage(0); }, [q]);
  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">Ticketing</div><div className="page-subtitle">History — new tickets created at invoicing stage</div></div>
      </div>
      <div className="card">
        <div style={{ marginBottom: 14 }}>
          <input className="form-input" placeholder="Search ref, PNR, ticket number, airline..." value={q} onChange={e => setQ(e.target.value)} style={{ maxWidth: 380 }} />
        </div>
        {loading ? <div className="spinner" /> : (
          <>
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Ref</th><th>Client</th><th>Airline</th><th>PNR</th><th>Route</th><th>Departure</th><th>Class</th><th style={{ textAlign: 'right' }}>Cost</th><th style={{ textAlign: 'right' }}>Sell</th><th>Status</th><th>Invoice</th><th></th></tr></thead>
                <tbody>
                  {tickets.map(t => (
                    <tr key={t.id}>
                      <td style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--primary)' }}>{t.ticket_ref}</td>
                      <td>{t.clients?.full_name || ''}</td>
                      <td style={{ fontWeight: 500 }}>{t.airline || ''}</td>
                      <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{t.pnr || ''}</td>
                      <td>{t.route_from || ''}{t.route_to ? ' → ' + t.route_to : ''}</td>
                      <td>{t.departure_date ? new Date(t.departure_date).toLocaleDateString() : ''}</td>
                      <td>{t.cabin_class || ''}</td>
                      <td style={{ textAlign: 'right' }} className="amount">{t.cost_currency?.code} {parseFloat(t.cost_price || 0).toLocaleString()}</td>
                      <td style={{ textAlign: 'right' }} className="amount">{t.sell_currency?.code} {parseFloat(t.sell_price || 0).toLocaleString()}</td>
                      <td><span className={`badge ${statusColor[t.ticket_status] || 'badge-gray'}`}>{t.ticket_status}</span></td>
                      <td>{t.invoice ? <Link to={`/invoices/${t.invoice.id}`} style={{ color: 'var(--primary)', fontSize: 12, fontWeight: 600 }}>{t.invoice.invoice_number}</Link> : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>}</td>
                      <td><Link to={`/tickets/${t.id}`} className="btn btn-secondary btn-sm">View</Link></td>
                    </tr>
                  ))}
                  {tickets.length === 0 && <tr><td colSpan={11}><div className="empty-state"><div className="empty-state-title">No tickets found</div></div></td></tr>}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, fontSize: 13, color: 'var(--text-muted)' }}>
                <span>{total} records &nbsp;·&nbsp; Page {page + 1} of {totalPages}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(0)} disabled={page === 0}>«</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>‹ Prev</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>Next ›</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
